import { marked } from 'marked';

import { ARTIFACT_PLACEHOLDER_TAG, REF_PLACEHOLDER_TAG } from '../_threadId/components/format-content';

const KIM_MARKED_OPTIONS = { async: false, breaks: true } as const;

// Model and document text is untrusted, and marked passes raw HTML straight through. Only the
// markdown output tags and our placeholder tags survive; anything else is unwrapped to its text.
const ALLOWED_TAGS = new Set([
  'a',
  'b',
  'blockquote',
  'br',
  'code',
  'del',
  'em',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'i',
  'li',
  'ol',
  'p',
  'pre',
  's',
  'strong',
  'sub',
  'sup',
  'table',
  'tbody',
  'td',
  'th',
  'thead',
  'tr',
  'u',
  'ul',
  REF_PLACEHOLDER_TAG,
  ARTIFACT_PLACEHOLDER_TAG,
]);

// Removed together with their content: these load remote resources, run code or submit data.
const DROPPED_TAGS = new Set([
  'audio',
  'base',
  'button',
  'canvas',
  'embed',
  'form',
  'frame',
  'frameset',
  'iframe',
  'img',
  'input',
  'link',
  'math',
  'meta',
  'noembed',
  'noframes',
  'noscript',
  'object',
  'picture',
  'script',
  'select',
  'source',
  'style',
  'svg',
  'template',
  'textarea',
  'title',
  'track',
  'video',
  'xmp',
]);

const ALLOWED_ATTRIBUTES: Record<string, ReadonlySet<string>> = {
  a: new Set(['href', 'title']),
  ol: new Set(['start']),
  th: new Set(['align']),
  td: new Set(['align']),
  [REF_PLACEHOLDER_TAG]: new Set(['data-id', 'data-ref-type']),
  [ARTIFACT_PLACEHOLDER_TAG]: new Set(['data-id']),
};

const ALLOWED_LINK_PROTOCOLS = new Set(['http:', 'https:', 'mailto:']);

function isAllowedLink(href: string) {
  try {
    return ALLOWED_LINK_PROTOCOLS.has(new URL(href).protocol);
  } catch {
    return false;
  }
}

function sanitiseChildren(parent: ParentNode) {
  for (const node of Array.from(parent.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) continue;
    if (node.nodeType !== Node.ELEMENT_NODE) {
      node.remove();
      continue;
    }

    const element = node as Element;
    const tag = element.localName;
    if (DROPPED_TAGS.has(tag)) {
      element.remove();
      continue;
    }

    sanitiseChildren(element);

    if (!ALLOWED_TAGS.has(tag) || element.namespaceURI !== 'http://www.w3.org/1999/xhtml') {
      element.replaceWith(...Array.from(element.childNodes));
      continue;
    }

    for (const { name, value } of Array.from(element.attributes)) {
      const allowed = ALLOWED_ATTRIBUTES[tag]?.has(name) && (name !== 'href' || isAllowedLink(value));
      if (!allowed) element.removeAttribute(name);
    }
  }
}

/** Strips everything but markdown formatting and Kim placeholder tags from an HTML string. */
export function sanitiseMarkdownHtml(html: string): string {
  // Template content is inert, so nothing in it loads or runs while it is being cleaned.
  const template = document.createElement('template');
  template.innerHTML = html;
  sanitiseChildren(template.content);
  return template.innerHTML;
}

/** Renders Kim markdown to sanitised HTML, ready for html-react-parser or the clipboard. */
export function renderKimMarkdown(markdown: string): string {
  return sanitiseMarkdownHtml(marked.parse(markdown, KIM_MARKED_OPTIONS) as string);
}
